import React, { useState } from "react";
import { ChevronDown, Code, ArrowRight } from "../components/Icons";
import { Reveal, Button, SectionHeading, PageGlow } from "../components/ui";

const FAQS = [
  {
    category: "Ownership & Legal",
    items: [
      {
        q: "Who owns the code after delivery?",
        a: "You do — 100%, unconditionally. Upon final payment and deployment, all source code, assets, infrastructure configurations, documentation, and intellectual property transfer completely to the client. We sign a formal IP assignment agreement at project kickoff. No licensing fees, no usage restrictions, no vendor lock-in. The code is yours to host anywhere, modify freely, and sell or sublicense as you see fit.",
      },
      {
        q: "Do you use open-source components that could create licensing risks?",
        a: "Yes, most modern software includes open-source dependencies — and we handle this carefully. We audit every dependency for license compatibility (MIT, Apache 2.0, BSD are standard; we avoid GPL/LGPL for commercial products). You receive a full Software Bill of Materials (SBOM) listing every dependency and its license. We flag any copyleft risks before development begins.",
      },
      {
        q: "What about confidentiality? Do you sign NDAs?",
        a: "Absolutely. We sign a mutual NDA before any detailed discussion of your project. Your business logic, technical specifications, user data, and competitive positioning remain strictly confidential. Our engineers sign individual NDAs as part of their employment contracts. We do not disclose client names without explicit written consent — though many clients choose to be featured as portfolio references.",
      },
    ],
  },
  {
    category: "The 15-Day Process",
    items: [
      {
        q: "How do you ship production-grade software in 15 days without technical debt?",
        a: "The short answer: process, AI tooling, and specialization. Here's the breakdown — Days 1-3 are entirely pre-code: we use AI-assisted schema generators to produce database models, API contracts, and architectural diagrams before writing a single line of code. This eliminates ambiguity that causes rework. Days 4-10 use parallel engineering streams (frontend, backend, mobile, infra simultaneously) rather than sequential waterfall. Days 11-13 are dedicated to load testing, security hardening, and optimization — not new features. This discipline is what prevents debt. Senior engineers review every output from AI generators. Nothing ships without human review.",
      },
      {
        q: "What if my project is genuinely complex? Can you still ship in 15 days?",
        a: "15 days is our standard for well-scoped products. For multi-system enterprise projects (multi-tenant SaaS, complex ERP integrations, or apps requiring regulatory compliance), timelines extend based on scope. We're transparent about this in the initial architecture review. What remains constant: our process eliminates the padding and delays that plague most development projects. A project that typically takes 6 months conventionally might take us 45 days. We don't pad timelines to manage expectations — we give accurate estimates and hit them.",
      },
      {
        q: "What's your client communication process during development?",
        a: "We operate async-first to respect your time. You'll receive a shared Notion workspace with the full project plan, daily written progress updates, and Loom video walkthroughs at each major milestone (end of blueprint phase, backend API completion, staging deployment). You have direct async access to the lead engineer at all times. We hold one structured video review call per phase — three in total — where you provide feedback. This isn't a black-box development experience.",
      },
      {
        q: "How detailed does my brief need to be before you start?",
        a: "You don't need a full specification document — that's our job. We need your core concept, target users, key workflows (what the user does in the app), and any hard constraints (budget, existing systems to integrate with, regulatory requirements). Our Day 1-3 blueprint phase converts your vision into a precise technical specification. We've found that over-specified briefs from non-technical clients often constrain better architectural decisions, so we prefer starting with intent rather than technical prescriptions.",
      },
    ],
  },
  {
    category: "Post-Launch & Maintenance",
    items: [
      {
        q: "What happens after Day 15?",
        a: "Day 15 isn't the end — it's the beginning. We offer three post-launch tiers: (1) Handoff Package — comprehensive documentation, runbooks, and a 2-hour technical walkthrough so your team can self-maintain. (2) SLA Maintenance Tier — we handle production incidents, security patches, dependency updates, and minor feature additions on a monthly retainer. (3) Full Scaling Partnership — we embed as your extended engineering team, handling product evolution, infrastructure scaling, and new feature development on an ongoing basis. Most clients start with Tier 2 and graduate to Tier 3 as they grow.",
      },
      {
        q: "How do you handle scaling? What if we suddenly go viral?",
        a: "Kubernetes autoscaling is configured from Day 1 — not added later as an afterthought. We pre-configure Horizontal Pod Autoscalers to scale services within 90 seconds of traffic spikes. Database read replicas are provisioned for read-heavy workloads. CDN layers (CloudFront or Cloudflare) serve static assets globally. Load testing at 10× expected peak is mandatory before launch. During our 24-hour post-launch monitoring window, we actively watch metrics dashboards and can scale infrastructure manually within minutes if needed. We've never had a client go down on launch day.",
      },
      {
        q: "Can I hire your engineers directly after the project?",
        a: "Our engineers are full-time Dowble employees, and we don't facilitate placement of our team members to clients during or within 12 months of an engagement. This isn't adversarial — it's how we ensure our team remains intact and focused on delivering for all clients. If you want ongoing engineering resources, we recommend our Scaling Partnership tier, which gives you dedicated Dowble engineers without the overhead of hiring, onboarding, and managing full-time staff.",
      },
    ],
  },
  {
    category: "Pricing & Engagement",
    items: [
      {
        q: "How is pricing structured?",
        a: "We don't publish fixed price lists because every project has a unique scope. Pricing is based on product complexity, number of platforms (mobile + web + desktop), backend architecture complexity, and post-launch support tier. After your initial brief, we provide a fixed-price proposal — not a time-and-materials estimate — within 48 hours. This means no surprise invoices. Payment is structured as 40% at kickoff (after NDA and proposal acceptance), 40% at staging deployment (Day 11), and 20% at production deployment (Day 15).",
      },
      {
        q: "What's the minimum engagement?",
        a: "Our minimum engagement is a single complete product — mobile app, web platform, or desktop application. We don't do partial work (e.g., just backend or just UI design in isolation). The 15-day pipeline is designed as an integrated end-to-end process, and breaking it into disconnected parts undermines what makes it fast and high-quality. If you only need specific engineering help (code review, architecture consulting, performance optimization), we do offer advisory retainers separate from product builds.",
      },
    ],
  },
];

function FAQItem({ item }) {
  const [open, setOpen] = useState(false);

  return (
    <div className={"rounded-2xl border transition-all duration-300 " + (open ? "border-zinc-300 bg-zinc-100" : "border-zinc-200 bg-white hover:bg-zinc-50")}>
      <button
        className="w-full flex items-start justify-between gap-4 p-6 text-left"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="text-zinc-900 font-semibold text-base leading-snug">{item.q}</span>
        <span className={"flex-shrink-0 mt-0.5 transition-all duration-300 " + (open ? "text-accent-600 rotate-180" : "text-zinc-500")}>
          <ChevronDown size={20} />
        </span>
      </button>
      {/* Fluid expand/collapse via grid-rows trick */}
      <div
        className="grid transition-[grid-template-rows] duration-300 ease-out"
        style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <div className="px-6 pb-6">
            <div className="hr-soft mb-5" />
            <p className="text-zinc-600 text-sm leading-relaxed">{item.a}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FAQPage({ navigate }) {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative py-20 px-6 text-center overflow-hidden">
        <PageGlow />
        <div className="relative max-w-4xl mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="Engine Room"
              title="Technical FAQ"
              subtitle="Straightforward answers to the questions sophisticated clients ask before committing to a build. No marketing fluff — just honest technical clarity."
            />
          </Reveal>
        </div>
      </section>

      {/* Quick links */}
      <div className="border-y border-zinc-200 py-5 px-6">
        <div className="max-w-4xl mx-auto flex flex-wrap gap-3">
          {FAQS.map((cat) => (
            <span key={cat.category} className="badge">{cat.category}</span>
          ))}
        </div>
      </div>

      {/* FAQ Sections */}
      <section className="py-16 px-6" aria-label="Frequently asked questions">
        <div className="max-w-4xl mx-auto space-y-14">
          {FAQS.map((section) => (
            <Reveal key={section.category}>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px flex-1 bg-zinc-100" />
                <h2 className="text-accent-600 text-xs font-bold uppercase tracking-[0.18em] whitespace-nowrap">
                  {section.category}
                </h2>
                <div className="h-px flex-1 bg-zinc-100" />
              </div>
              <div className="space-y-3">
                {section.items.map((item) => (
                  <FAQItem key={item.q} item={item} />
                ))}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Still have questions CTA */}
      <section className="border-t border-zinc-200 py-20 px-6 text-center">
        <Reveal className="max-w-2xl mx-auto">
          <span className="inline-flex w-14 h-14 rounded-2xl bg-accent-500/12 border border-accent-500/25 items-center justify-center text-accent-600 mb-6">
            <Code size={26} />
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 mb-3">Still have a specific question?</h2>
          <p className="text-zinc-600 text-sm mb-7 max-w-lg mx-auto">
            We're happy to get on a call or answer async via email. Most questions get a response within 4 hours.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" onClick={() => navigate("contact")}>
              Ask Us Directly
              <ArrowRight size={16} />
            </Button>
            <Button size="lg" variant="secondary" href="mailto:contact@dowble.com">
              Email contact@dowble.com
            </Button>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
